import { useCallback, useEffect, useMemo, useState } from "react";
import JobLog from "../components/JobLog";
import {
  getGavPedidos, getGavIndicadores, getGavExecucoes, getGavConfig,
  runGav, solicitarGav, marcarFeedbackVisto,
} from "../api/client";

// GAV — Gerenciador de Avaliações (Amazon). Nenhum dado do comprador aparece aqui: o GAV não guarda nenhum.

const STATUS = {
  AGUARDANDO_ENTREGA: { label: "Aguardando entrega", cls: "bg-gray-100 text-gray-700" },
  AGUARDANDO_JANELA:  { label: "Aguardando janela",  cls: "bg-blue-100 text-blue-800" },
  ENVIANDO:           { label: "Enviando",           cls: "bg-purple-100 text-purple-800" },
  ENVIADA:            { label: "Enviada",            cls: "bg-green-100 text-green-800" },
  INDISPONIVEL:       { label: "Indisponível",       cls: "bg-yellow-100 text-yellow-800" },
  ERRO:               { label: "Erro",               cls: "bg-red-100 text-red-800" },
  EXPIRADA:           { label: "Expirada",           cls: "bg-orange-100 text-orange-800" },
  NAO_ELEGIVEL:       { label: "Não elegível",       cls: "bg-gray-100 text-gray-400" },
};
const ORIGEM = { GAV_AUTO: "GAV (auto)", GAV_MANUAL: "GAV (manual)", SELLER_CENTRAL: "Seller Central" };
const ENTREGA = {
  Delivered: "Entregue", PickedUp: "Coletado", DroppedOff: "Postado", PendingSchedule: "Agendando",
  PendingPickUp: "Aguardando coleta", PendingDropOff: "Aguardando postagem", LabelCanceled: "Etiqueta cancelada",
  OutForDelivery: "Saiu p/ entrega", InTransit: "Em trânsito", RejectedByBuyer: "Recusado",
  Undeliverable: "Não entregável", ReturningToSeller: "Devolvendo", ReturnedToSeller: "Devolvido",
  Lost: "Extraviado", Damaged: "Avariado",
};
const PODE_SOLICITAR = ["AGUARDANDO_JANELA", "INDISPONIVEL", "ERRO"];

// Datas vêm do backend no horário de São Paulo, sem fuso ("2026-09-15T20:00:00") — formata sem converter
const fmtData = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : "—");
const fmtDataHora = (iso) => (iso ? `${fmtData(iso)} ${iso.slice(11, 16)}` : "—");
const hojeISO = () => new Date().toISOString().slice(0, 10);
const diasAtrasISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

function Badge({ status }) {
  const s = STATUS[status] || { label: status || "—", cls: "bg-gray-100 text-gray-600" };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${s.cls}`}>{s.label}</span>;
}

function Kpi({ label, valor, sub, cor = "text-gray-800" }) {
  return (
    <div className="bg-white rounded-xl shadow p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${cor}`}>{valor}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function entregaTexto(p) {
  if (p.canal === "FBA") return p.order_status === "Shipped" ? "Enviado (FBA)" : p.order_status || "—";
  if (p.order_status === "Canceled") return "Cancelado";
  return ENTREGA[p.easyship_status] || p.easyship_status || p.order_status || "—";
}

export default function GAV() {
  const [filtros, setFiltros] = useState({ inicio: diasAtrasISO(60), fim: hojeISO(), status: "", canal: "", q: "" });
  const [pedidos, setPedidos] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [execucoes, setExecucoes] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [jobId, setJobId] = useState(null);
  const [jobLabel, setJobLabel] = useState("");
  const [running, setRunning] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro("");
    const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
    Promise.all([
      getGavPedidos(params),
      getGavIndicadores({ inicio: filtros.inicio, fim: filtros.fim }),
      getGavExecucoes(),
      getGavConfig(),
    ])
      .then(([p, k, e, c]) => {
        setPedidos(p.data.pedidos);
        setKpi(k.data);
        setExecucoes(e.data);
        setConfig(c.data);
      })
      .catch((err) => setErro(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const iniciar = async (fn, label) => {
    setErro("");
    setJobId(null);
    setRunning(true);
    try {
      const { data } = await fn();
      setJobLabel(label);
      setJobId(data.job_id);
    } catch (err) {
      setRunning(false);
      setErro(err.response?.data?.detail || err.message);
    }
  };

  const rodarReal = () => {
    if (!window.confirm("Enviar de verdade a solicitação de avaliação para todos os pedidos liberados? Cada pedido recebe no máximo uma, para sempre.")) return;
    iniciar(() => runGav("real"), "Execução real");
  };

  const solicitar = (p) => {
    const real = config && !config.dry_run;
    const msg = real
      ? `Enviar agora a solicitação de avaliação do pedido ${p.amazon_order_id}? Não dá pra desfazer nem reenviar.`
      : `Simular (dry-run) a solicitação do pedido ${p.amazon_order_id}?`;
    if (!window.confirm(msg)) return;
    iniciar(() => solicitarGav(p.amazon_order_id), `Solicitar ${p.amazon_order_id}${real ? "" : " (dry-run)"}`);
  };

  const feedbackNovos = useMemo(
    () => pedidos.flatMap((p) => p.feedback.filter((f) => !f.visto).map((f) => ({ ...f, pedido: p.amazon_order_id }))),
    [pedidos]
  );

  const set = (k) => (e) => setFiltros((f) => ({ ...f, [k]: e.target.value }));
  const dryRun = config ? config.dry_run : true;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Avaliações Amazon</h2>
          <p className="text-sm text-gray-500">
            Solicitação oficial de avaliação (a mesma do botão "Solicitar uma Revisão"), uma por pedido.
            {config && <> Execução diária às {config.horario} ({config.tz}){!config.enabled && " — desligada"}.</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${dryRun ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}>
            {dryRun ? "Modo DRY-RUN — nada é enviado" : "Modo REAL — envia de verdade"}
          </span>
          <button type="button" onClick={() => iniciar(() => runGav("dry-run"), "Dry-run")} disabled={running}
            className="bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 text-sm px-4 py-2 rounded-lg">
            Rodar agora (dry-run)
          </button>
          <button type="button" onClick={rodarReal} disabled={running || dryRun}
            title={dryRun ? "Desligue o dry-run em Configurações → GAV para enviar de verdade" : ""}
            className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-semibold px-4 py-2 rounded-lg">
            Rodar agora (real)
          </button>
          <button type="button" onClick={carregar}
            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm px-3 py-2 rounded-lg">
            ↻
          </button>
        </div>
      </div>

      {erro && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{erro}</div>}

      {jobId && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-1">{jobLabel}</p>
          <JobLog jobId={jobId} onDone={() => { setRunning(false); carregar(); }} />
        </div>
      )}

      {feedbackNovos.length > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
          <div className="flex items-center justify-between gap-3">
            <strong>{feedbackNovos.length} feedback(s) negativo/neutro novo(s)</strong>
            <button type="button" onClick={() => marcarFeedbackVisto().then(carregar)}
              className="text-xs underline hover:no-underline">Marcar como vistos</button>
          </div>
          <ul className="mt-1 space-y-0.5">
            {feedbackNovos.map((f) => (
              <li key={`${f.pedido}-${f.data}`}>{f.nota}★ — pedido {f.pedido} ({fmtData(f.data)}){f.comentario && `: "${f.comentario}"`}</li>
            ))}
          </ul>
        </div>
      )}

      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <Kpi label="Pedidos no período" valor={kpi.pedidos} />
          <Kpi label="Com solicitação enviada" valor={`${kpi.pct_enviadas}%`} sub={`${kpi.enviadas} pedido(s)`} cor="text-green-700" />
          <Kpi label="Enviadas pelo GAV" valor={kpi.enviadas_gav} />
          <Kpi label="Expiradas sem envio" valor={kpi.expiradas} cor={kpi.expiradas ? "text-orange-600" : "text-gray-800"} />
          <Kpi label="Feedback 1–3★" valor={kpi.feedbacks_negativos} cor={kpi.feedbacks_negativos ? "text-red-600" : "text-gray-800"} />
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap items-end gap-3 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">De</span>
          <input type="date" value={filtros.inicio} onChange={set("inicio")} className="border border-gray-300 rounded-lg px-2 py-1.5" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Até</span>
          <input type="date" value={filtros.fim} onChange={set("fim")} className="border border-gray-300 rounded-lg px-2 py-1.5" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Status</span>
          <select value={filtros.status} onChange={set("status")} className="border border-gray-300 rounded-lg px-2 py-1.5">
            <option value="">Todos</option>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Canal</span>
          <select value={filtros.canal} onChange={set("canal")} className="border border-gray-300 rounded-lg px-2 py-1.5">
            <option value="">Todos</option>
            <option value="DBA">DBA (Easy Ship)</option>
            <option value="FBA">FBA</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 flex-1 min-w-[12rem]">
          <span className="text-xs text-gray-500">Pedido ou SKU</span>
          <input type="text" value={filtros.q} onChange={set("q")} placeholder="701-… ou EG-FK-…"
            className="border border-gray-300 rounded-lg px-2 py-1.5" />
        </label>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden mb-6">
        <div className="overflow-auto max-h-[70vh] scrollbar-visible">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase sticky top-0">
              <tr>
                {["Compra", "Pedido", "Produto", "Canal", "Entrega", "Janela", "Solicitação", "Enviada em", "Origem", "Feedback", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={11} className="px-3 py-6 text-center text-gray-400">Carregando...</td></tr>}
              {!loading && pedidos.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-6 text-center text-gray-400">Nenhum pedido no período.</td></tr>
              )}
              {!loading && pedidos.map((p) => (
                <tr key={p.amazon_order_id} className="hover:bg-gray-50 align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{fmtData(p.purchase_date)}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                    <a href={`https://sellercentral.amazon.com.br/orders-v3/order/${p.amazon_order_id}`} target="_blank"
                      rel="noreferrer" className="text-blue-700 hover:underline">{p.amazon_order_id}</a>
                    {p.is_replacement === 1 && <div className="text-[10px] text-gray-400">substituição</div>}
                  </td>
                  <td className="px-3 py-2 max-w-xs">
                    {p.itens.length === 0 && <span className="text-gray-400">—</span>}
                    {p.itens.map((i) => (
                      <div key={`${i.sku}-${i.asin}`} className="leading-tight mb-1">
                        <div className="truncate" title={i.titulo}>{i.titulo}</div>
                        <div className="text-xs text-gray-400">{i.sku}{i.quantidade > 1 && ` × ${i.quantidade}`}</div>
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2">{p.canal}</td>
                  <td className={`px-3 py-2 whitespace-nowrap ${p.entrega_atrasada ? "text-red-600 font-semibold" : ""}`}>
                    {entregaTexto(p)}
                    {p.entrega_atrasada && <div className="text-xs">atrasada (+10 dias)</div>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    {p.janela_inicio ? <>{fmtData(p.janela_inicio)} – {fmtData(p.janela_fim)}</> : "—"}
                    {p.liberado_em && p.liberado_em !== p.janela_inicio && p.status_solicitacao === "AGUARDANDO_JANELA" && (
                      <div className="text-gray-400">libera {fmtData(p.liberado_em)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Badge status={p.status_solicitacao} />
                    {p.ultimo_erro && p.status_solicitacao === "ERRO" && (
                      <div className="text-xs text-red-500 mt-1 max-w-[14rem]" title={p.ultimo_erro}>{p.ultimo_erro.slice(0, 60)}</div>
                    )}
                    {p.observacao && <div className="text-xs text-gray-400 mt-1 max-w-[14rem]">{p.observacao}</div>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDataHora(p.solicitado_em)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{ORIGEM[p.origem] || "—"}</td>
                  <td className="px-3 py-2">
                    {p.feedback.map((f) => (
                      <div key={f.data} className="text-xs text-red-600" title={f.comentario || ""}>{f.nota}★ {fmtData(f.data)}</div>
                    ))}
                  </td>
                  <td className="px-3 py-2">
                    {PODE_SOLICITAR.includes(p.status_solicitacao) && (
                      <button type="button" onClick={() => solicitar(p)} disabled={running}
                        className="text-xs text-blue-700 hover:underline disabled:text-gray-400 whitespace-nowrap">
                        Solicitar agora
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">{pedidos.length} pedido(s)</div>
      </div>

      <div className="bg-white rounded-xl shadow p-5">
        <h3 className="font-bold text-gray-800 mb-3">Execuções</h3>
        {execucoes.length === 0 && <p className="text-sm text-gray-400">Nenhuma execução ainda.</p>}
        {execucoes.length > 0 && (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase">
                <tr>
                  {["#", "Início", "Modo", "Origem", "Situação", "Sinc.", "Verif.", "Enviadas", "Seriam enviadas", "Indisp.", "Expiradas", "Feedback novo", "Erros"].map((h) => (
                    <th key={h} className="px-2 py-1 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {execucoes.map((e) => (
                  <tr key={e.id} className="align-top">
                    <td className="px-2 py-1">{e.id}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmtDataHora(e.inicio)}</td>
                    <td className="px-2 py-1">{e.modo}</td>
                    <td className="px-2 py-1">{e.origem}</td>
                    <td className={`px-2 py-1 ${e.situacao === "OK" ? "text-green-700" : e.situacao === "RODANDO" ? "text-blue-700" : "text-red-600"}`}>{e.situacao}</td>
                    <td className="px-2 py-1">{e.sincronizados}</td>
                    <td className="px-2 py-1">{e.verificados}</td>
                    <td className="px-2 py-1 font-semibold">{e.enviados}</td>
                    <td className="px-2 py-1">{e.seriam_enviados}</td>
                    <td className="px-2 py-1">{e.indisponiveis}</td>
                    <td className="px-2 py-1">{e.expirados}</td>
                    <td className="px-2 py-1">{e.feedbacks_novos}</td>
                    <td className="px-2 py-1">
                      {e.erros > 0 ? (
                        <details>
                          <summary className="text-red-600 cursor-pointer">{e.erros}</summary>
                          <pre className="text-xs text-red-700 whitespace-pre-wrap max-w-xl">{e.resumo_erros}</pre>
                        </details>
                      ) : 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
